/**
 * Client for the Backstage organisation import (`/api/admin/org-import`). Preview reads a pasted catalogue and reports what
 * applying it would change; apply repeats that work server-side and refuses unless the plan still matches the preview.
 *
 * Unlike the shared `adminRequest` helper, this module keeps the `errors` list the server returns with a 400 (an invalid file)
 * and a 409 (a conflict), because the administrator needs every problem, not only the summary.
 */
import { apiFetch } from "./apiFetch";

/** One change an import would make to a unit. */
export interface OrganisationImportChange {
  kind: "Tag" | "Create" | "Rename" | "Reparent" | "SetManager";
  detail: string;
}

/** One unit in the file and what the import would do with it. */
export interface OrganisationImportNode {
  backstageName: string;
  title: string;
  kind: "Division" | "Department" | "Group";
  parentName: string;
  depth: number;
  managerEmail: string | null;
  status: "Unchanged" | "Changed" | "Conflict";
  matchedId: string | null;
  conflictReason: string | null;
  changes: OrganisationImportChange[];
}

/** An imported row the file no longer names. It is reported and never removed. */
export interface OrganisationImportNotInSource {
  id: string;
  kind: "Division" | "Department" | "Group";
  backstageName: string;
  title: string;
}

/** What an organisation import would do. Nothing has been changed. */
export interface OrganisationImportPlan {
  parseErrors: string[];
  canApply: boolean;
  changeCount: number;
  fingerprint: string | null;
  nodes: OrganisationImportNode[];
  managersToCreate: string[];
  notInSource: OrganisationImportNotInSource[];
}

/** The outcome of applying an import. */
export interface OrganisationImportApplied {
  changesApplied: number;
  fingerprint: string;
  /** Steps the import skipped without failing, such as a manager role that does not exist. */
  warnings: string[];
}

/** The outcome of one import call. */
export type OrganisationImportResult<TResponse> =
  | { kind: "ok"; response: TResponse }
  | { kind: "denied" }
  | { kind: "invalid"; errors: string[] }
  | { kind: "conflict"; message: string; errors: string[] }
  | { kind: "error"; message: string };

const previewPath = "/api/admin/org-import/preview";
const applyPath = "/api/admin/org-import/apply";

interface ProblemBody {
  detail?: string;
  title?: string;
  errors?: string[];
}

async function importRequest<TResponse>(path: string, body: unknown, label: string): Promise<OrganisationImportResult<TResponse>> {
  try {
    const response = await apiFetch(path, {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (response.status === 403) {
      return { kind: "denied" };
    }

    if (response.status === 200) {
      return { kind: "ok", response: (await response.json()) as TResponse };
    }

    const problem = await readProblem(response);
    if (response.status === 400) {
      return { kind: "invalid", errors: problem.errors ?? [problem.detail ?? `The ${label} request was not valid.`] };
    }

    if (response.status === 409) {
      return { kind: "conflict", message: problem.detail ?? `The ${label} was refused.`, errors: problem.errors ?? [] };
    }

    return { kind: "error", message: problem.detail ?? `The ${label} endpoint returned ${String(response.status)}.` };
  } catch {
    return { kind: "error", message: `The ${label} endpoint could not be reached.` };
  }
}

async function readProblem(response: Response): Promise<ProblemBody> {
  try {
    return (await response.json()) as ProblemBody;
  } catch {
    return {};
  }
}

/**
 * Reads a catalogue file and returns what applying it would change. Changes nothing.
 *
 * @param yaml - The Backstage catalogue text.
 * @returns The plan, or the list of problems when the file is not valid.
 */
export function previewOrganisationImport(yaml: string): Promise<OrganisationImportResult<OrganisationImportPlan>> {
  return importRequest<OrganisationImportPlan>(previewPath, { yaml }, "organisation import preview");
}

/**
 * Applies a catalogue file exactly as previewed. The server refuses when the plan has changed since the preview.
 *
 * @param yaml - The Backstage catalogue text that was previewed.
 * @param planFingerprint - The fingerprint of the previewed plan.
 * @returns The number of changes made, or why the import was refused.
 */
export function applyOrganisationImport(yaml: string, planFingerprint: string): Promise<OrganisationImportResult<OrganisationImportApplied>> {
  return importRequest<OrganisationImportApplied>(applyPath, { yaml, planFingerprint }, "organisation import");
}
